import React from 'react';
import { withTranslation } from 'react-i18next';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PageHeader from '../boxes/PageHeader';
import PageBody from '../boxes/PageBody';
import MainContent from '../boxes/MainContent';
import DataTable from '../boxes/DataTable';
import PolicyStatusPill from '../boxes/PolicyStatusPill';
import PolicyDetailModal from '../boxes/PolicyDetailModal';
import NotificationBanner from '../boxes/NotificationBanner';
import clientServices from '../../services/client';
import { downloadBlob } from '../../services/utilityService';
import { formatPolicyNumber, formatPolicyDate } from '../../utils/policyDisplay';

const DATE_CELL_STYLE = { color: '#4F4F4F' };

//adding comment to rebuild

const MENU_SX = {
    minWidth: '200px',
    borderRadius: '10px',
    boxShadow: '0px 4px 12px 0px #1B1C1D26',
    '& .MuiList-root': { padding: '0.8rem 0' },
    '& .MuiMenuItem-root': {
        gap: '12px',
        padding: '8px 12px',
        fontSize: '1.3rem',
        color: '#1c1b1b'
    },
    '& .MuiMenuItem-root .MuiListItemIcon-root': { minWidth: 'auto', color: '#4f4f4f' },
    '& .MuiSvgIcon-root': { fontSize: '16px' }
};

class PolicyListComponent extends React.Component {
    state = { viewedPolicy: null, downloadingPolicyId: null, downloadError: null };

    constructor(props) {
        super(props);
        this._isMounted = false;
        this._downloadingPolicyId = null;
    }

    componentDidMount() {
        this._isMounted = true;
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    safeSetState = (update) => {
        if (this._isMounted) this.setState(update);
    };

    openPolicyDetail = (id, policy) => this.setState({ viewedPolicy: policy });

    closePolicyDetail = () => this.setState({ viewedPolicy: null });

    closeDownloadError = () => this.setState({ downloadError: null });

    handleDownloadCertificate = (policy) => {
        const { user, t } = this.props;
        if (this._downloadingPolicyId) return;
        this._downloadingPolicyId = policy.id;

        const { accessToken } = user.credentials;
        this.safeSetState({ downloadingPolicyId: policy.id, downloadError: null });

        clientServices
            .getPolicyCertificate(accessToken, user.companyInfo.id, policy.id)
            .then(({ blob, filename }) => {
                downloadBlob(
                    blob,
                    filename || `Certificate-${formatPolicyNumber(policy.reference_code)}.pdf`
                );
            })
            .catch((err) => {
                console.error('Certificate download failed:', err);
                this.safeSetState({
                    downloadError: t(
                        'policies.downloadErrorMessage',
                        "We couldn't download your certificate. Please try again."
                    )
                });
            })
            .finally(() => {
                this._downloadingPolicyId = null;
                this.safeSetState({ downloadingPolicyId: null });
            });
    };

    fetchPolicies = (page, filter, limit) => {
        const { user } = this.props;

        const { accessToken } = user.credentials;

        return clientServices
            .getPolicies(accessToken, user.companyInfo.id, {
                page,
                pageSize: limit
            })
            .then((result) => ({
                data: (result && result.policies) || [],
                pagination: {
                    current_page: (result && result.page) || page,
                    last_page: (result && result.total_pages || result.totalPages ) || 1
                }
            }));
    };

    formatDate = (date) => {
        const { t } = this.props;

        return <span style={DATE_CELL_STYLE}>{formatPolicyDate(t, date)}</span>;
    };

    getPolicyColumnData() {
        const { t } = this.props;

        return [
            {
                key: 'reference_code',
                type: 'string',
                alias: t('policies.dataTablePolicyNumberColumnAlias'),
                format: (data) => (
                    <span style={{ fontWeight: 600 }}>
                        {formatPolicyNumber(data.reference_code)}
                    </span>
                )
            },
            {
                key: 'start_date',
                type: 'string',
                alias: t('policies.dataTableStartDateColumnAlias'),
                format: (data) => this.formatDate(data.start_date)
            },
            {
                key: 'end_date',
                type: 'string',
                alias: t('policies.dataTableEndDateColumnAlias'),
                format: (data) => this.formatDate(data.end_date)
            },

            {
                key: 'policy_status',
                type: 'string',
                alias: t('policies.dataTableStatusColumnAlias'),
                format: (data) => <PolicyStatusPill status={data.policy_status} />
            }
        ];
    }

    render() {
        const { t } = this.props;
        const { viewedPolicy, downloadingPolicyId, downloadError } = this.state;

        return (
            <MainContent className="policies-page">
                <PageHeader title={t('policies.pageTitle')} />
                {downloadError && !viewedPolicy && (
                    <NotificationBanner
                        title={t('common.errorNotificationTitle', 'Error')}
                        message={downloadError}
                        type="error"
                        close={this.closeDownloadError}
                    />
                )}
                <PageBody>
                    <div className="policies-list__card">
                        <DataTable
                            fetchData={this.fetchPolicies}
                            tableClassName="dashboard__table--light-header"
                            tableColumnData={this.getPolicyColumnData()}
                            menuSx={MENU_SX}
                            emptyStateMessage={t('policies.emptyTableMessage')}
                            options={[
                                {
                                    value: 'View Policy',
                                    label: t('policies.viewPolicyMenuOptionLabel'),
                                    icon: <VisibilityOutlinedIcon fontSize="small" />,
                                    onClick: this.openPolicyDetail
                                },
                                {
                                    value: 'Download Certificate',
                                    label: t('policies.downloadCertificateMenuOptionLabel'),
                                    icon: <FileDownloadOutlinedIcon fontSize="small" />,
                                    onClick: (id, policy) => this.handleDownloadCertificate(policy)
                                }
                            ]}
                        />
                    </div>
                    {viewedPolicy && (
                        <PolicyDetailModal
                            policy={viewedPolicy}
                            onClose={this.closePolicyDetail}
                            onDownload={this.handleDownloadCertificate}
                            isDownloading={downloadingPolicyId === viewedPolicy.id}
                            downloadError={downloadError}
                            onDismissDownloadError={this.closeDownloadError}
                        />
                    )}
                </PageBody>
            </MainContent>
        );
    }
}

export default withTranslation()(PolicyListComponent);
